import {AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {Store} from "@ngrx/store";
import {FundInvestmentActions} from "../../store/fund.actions";
import {selectSelectedFundInvestmentsForFundReport} from "../../store/fund-investment/fund-investment.selectors";
import {selectAssetNameAndArchivedFromId, selectUnarchivedAssets} from "../../../asset/store/asset/asset.selectors";
import {AssetActions, AssetListActions} from "../../../asset/store/asset.actions";
import {EMPTY_FUND_INVESTMENT, FundInvestment} from "../../models/fund-investement";
import {BehaviorSubject, combineLatest, map, Observable, Subscription} from "rxjs";
import {Asset} from "../../../asset/models/asset/asset";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {selectReportDate} from "../../store/fund-report/fund-report.selectors";
import {MatTableDataSource} from "@angular/material/table";
import {MatSort} from "@angular/material/sort";
import {CodeTableEnum} from "../../../shared/model/code";
import {MatDialog} from "@angular/material/dialog";
import {ConfirmationComponent} from "../../../shared/components/confirmation/confirmation.component";
import {DateUtil} from "../../../shared/utils/date-util";
import {MatPaginator} from "@angular/material/paginator";

@Component({
    selector: "valumize-fund-investment-new-dialog",
    templateUrl: "./fund-investment-new-dialog.component.html",
    styleUrls: ["./fund-investment-new-dialog.component.scss"],
    standalone: false
})
export class FundInvestmentNewDialogComponent implements OnInit, AfterViewInit, OnDestroy {

    @Input() fundId?: number;
    @Input() fundReportId?: number;
    @Input() fundValuationId?: number;

    @ViewChild(MatSort, {static: true}) selectableAssetSort = new MatSort();
    @ViewChild("selectableAssetsPaginator") selectableAssetsPaginator!: MatPaginator;

    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly cdr = inject(ChangeDetectorRef);

    subscriptions: Subscription[] = [];

    selectableAssetColumns = ["id", "name", "country", "currencyIso", "activityDescription", "gic", "add"];
    partnershipInvestmentColumns = ["assetId", "assetName", "ownershipStake", "investmentDate", "remove"];
    selectableAssetDataSource: MatTableDataSource<any> = new MatTableDataSource();
    codeTableCountry = CodeTableEnum.SHARED_COUNTRY;
    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY;

    reportDate$ = this.store.select(selectReportDate);

    fundInvestments: FundInvestment[] = [];
    allAssets: Asset[] = [];
    newInvestmentsForm: { [key: number]: FormGroup } = {};
    newInvestments: Set<number> = new Set();
    assetSearchValue?: string;

    fundInvestmentsSubject = new BehaviorSubject<FundInvestment[]>(this.fundInvestments);
    fundInvestments$ = this.fundInvestmentsSubject.asObservable();

    updateFundInvestments(newInvestments: FundInvestment[]) {
        this.fundInvestments = newInvestments;
        this.fundInvestmentsSubject.next(newInvestments);
    }

    constructor( ) {
        this.store.dispatch(AssetListActions.load({}));
    }

    ngOnInit() {
        if (!!this.fundId && !!this.fundReportId) {
            this.store.dispatch(FundInvestmentActions.loadAll({fundId: this.fundId, fundReportId: this.fundReportId, fundValuationId: this.fundValuationId}));

            this.subscriptions.push(this.store.select(selectSelectedFundInvestmentsForFundReport).pipe(
                map((fundInvestments) => {
                    this.fundInvestments = fundInvestments.data;
                    this.fundInvestmentsSubject.next(this.fundInvestments);

                    this.fundInvestments.forEach(investment => {
                        this.newInvestmentsForm[investment.assetId] = this.formBuilder.group({
                            investmentDate: this.formBuilder.control<Date | null>(DateUtil.toJsDate(investment.investmentDate?.date) ?? null),
                            ownershipStake: this.formBuilder.control<number | null>(
                                DecimalFormatPipe.transformFractionToPercent(investment.ownershipStake?.fraction ?? undefined) ?? null, [Validators.max(100)])
                        });
                    });
                })).subscribe());

            this.subscriptions.push(combineLatest([this.fundInvestments$, this.store.select(selectUnarchivedAssets)]).pipe(
                map(([fundInvestments, assets]) => {
                    this.allAssets = assets;
                    this.selectableAssetDataSource.data = this.filterNotInvested(assets, fundInvestments);
                    this.selectableAssetDataSource.paginator = this.selectableAssetsPaginator;
                    this.selectableAssetDataSource.sort = this.selectableAssetSort;
                    this.selectableAssetDataSource.filterPredicate = (data: Asset, filter: string) => (data.name.text?.toLowerCase() ?? "").includes(filter);

                    this.selectableAssetDataSource.sortingDataAccessor = (item, property) => {
                        switch (property) {
                            case "name":
                                return item.name.text;
                            case "country":
                                return item.country.code;
                            case "currencyIso":
                                return item.currencyIso.code;
                            case "activityDescription":
                                return item.activityDescription;
                            case "gic":
                                return item.gic.code;
                            default:
                                return item[property as keyof Asset];
                        }
                    };
                    this.selectableAssetDataSource.sort.sort({
                        id: "name",
                        start: "desc",
                        disableClear: true
                    });
                })).subscribe());
        }
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    getAssetNameAndArchivedById(assetId: number): Observable<{ name: string; isArchived: boolean }> {
        return this.store.select(selectAssetNameAndArchivedFromId(assetId));
    }

    applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.assetSearchValue = filterValue;
        this.selectableAssetDataSource.filter = filterValue.trim().toLowerCase();
    }

    addFundInvestment(assetId: number) {
        const fundInvestmentToAdd: FundInvestment = {
            ...EMPTY_FUND_INVESTMENT,
            assetId
        };
        this.updateFundInvestments([...this.fundInvestments, fundInvestmentToAdd]);
        this.newInvestmentsForm[assetId] = this.formBuilder.group({
            investmentDate: this.formBuilder.control<Date | null>(null),
            ownershipStake: this.formBuilder.control<number | null>(null, {validators: Validators.max(100)})
        });
        this.newInvestments.add(assetId);
    }

    removeFundInvestment(assetId: number) {
        if (this.newInvestments.has(assetId)) {
            this.performRemoveFundInvestment(assetId);
        } else {
            this.openConfirmDialog(() => this.performRemoveFundInvestment(assetId));
        }
    }

    openConfirmDialog(deleteAction: () => void) {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = "This will delete this fund investment and its associated asset valuation. Are you sure?";

        dialogRef.afterClosed().pipe(map(result => {
            if (result) {
                deleteAction();
            }
        })).subscribe();
    }

    performRemoveFundInvestment(assetId: number) {
        this.updateFundInvestments(this.fundInvestments.filter(i => i.assetId !== assetId));
        delete this.newInvestmentsForm[assetId];
        this.newInvestments.delete(assetId);
    }

    filterNotInvested(assets: Asset[], fundInvestments: FundInvestment[]): Asset[] {
        return assets.filter(asset => !fundInvestments.map(f => f.assetId).includes(asset.id ?? -1));
    }

    save() {
        if (!this.fundId || !this.fundReportId) {
            return;
        }

        const isAnyFormInvalid = Object.values(this.newInvestmentsForm).some(form => form.invalid);

        if(!isAnyFormInvalid){
            const fundInvestmentsToSave = this.fundInvestments.map(investment => {
                const form = this.newInvestmentsForm[investment.assetId];
                if (form) {
                    return {
                        ...investment,
                        investmentDate: {
                            ...investment.investmentDate,
                            date: DateUtil.toIsoDate(form.value.investmentDate)
                        },
                        ownershipStake: {
                            ...investment.ownershipStake,
                            fraction: DecimalFormatPipe.transformPercentToFraction(form.value.ownershipStake)
                        },
                        isRealized: {
                            ...investment.isRealized,
                            value: investment.isRealized.value ?? false
                        }
                    };
                } else {
                    return investment;
                }
            });

            this.store.dispatch(FundInvestmentActions.saveAll({
                fundId: this.fundId,
                fundReportId: this.fundReportId,
                fundInvestments: fundInvestmentsToSave
            }));

            this.dialog.closeAll()
        }
    }

    getFormControl(assetId: number, controlName: string): FormControl {
        const form = this.newInvestmentsForm[assetId];
        return form ? form.get(controlName) as FormControl : new FormControl();
    }

    areAllFormsValid(): boolean {
        return Object.values(this.newInvestmentsForm).every(form => form.valid);
    }

    createAsset() {
        const assetName = this.assetSearchValue?.trim();
        if (!assetName) return;

        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = `You're about to create a company named "${assetName}". Do you want to continue?`

        dialogRef.afterClosed().pipe(map(result => {
            if (result) {
                this.store.dispatch(AssetActions.createInFundInvestmentDialog({assetName, preventNavigation: true}));
            }
        })).subscribe();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
